import { useCallback, useEffect, useRef, useState } from 'react';
import { listInvoiceBillingEvents } from '../components/pages/billing/services/billingApi';
import { listInvoices, type TitleDebtHistory, type TitleDebtHistoryInvoice } from '../components/pages/settlement/services/settlementApi';
import {
    buildInvoiceGroup,
    invoicesOfCharge,
    shownBillingEventIds,
    type InvoiceBillingEventGroup,
} from '../components/pages/recovery/shared/collectionBillingEvents';

const failureMessage = (error: unknown): string => {
    const text = error instanceof Error ? error.message : '';
    if (text.includes('403')) return 'Sem permissão para consultar a cobrança.';
    return 'Não foi possível consultar os eventos de cobrança agora.';
};

const fetchInvoiceGroup = async (
    invoice: TitleDebtHistoryInvoice,
    shownIds: Set<string>,
): Promise<InvoiceBillingEventGroup | null> => {
    const rows = await listInvoiceBillingEvents(invoice.invoice_id);
    return buildInvoiceGroup(invoice, rows, shownIds);
};

/**
 * Os eventos de cobrança de um título, por fatura, para a seção Cobrança do caso (AF-3990).
 *
 * O dossiê do `/history` só pendura evento em fatura que MOVIMENTOU o título; uma fatura em aberto
 * fica sem os seus. Este hook busca as faturas da operação no settlement, mantém as do charge do
 * título e lê os eventos de cada uma direto do billing (N+1, não há rota em lote), descartando os
 * que o `/history` já mostra (dedup por id). Guarda de corrida por geração, como `useTitleDebtHistory`.
 */
export default function useTitleBillingEvents(history: TitleDebtHistory | null) {
    const [groups, setGroups] = useState<InvoiceBillingEventGroup[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const generationRef = useRef(0);
    const historyRef = useRef(history);
    historyRef.current = history;

    const creditOperationId = history?.credit_operation_id ?? null;
    const chargeId = history?.charge_id ?? null;

    const load = useCallback(async () => {
        const generation = ++generationRef.current;
        const current = historyRef.current;
        setGroups([]);
        setError(null);
        if (!current || !creditOperationId || !chargeId) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const envelope = await listInvoices({ credit_operation_id: creditOperationId, size: '100' });
            const invoices = invoicesOfCharge(envelope.list ?? [], chargeId);
            const shownIds = shownBillingEventIds(current);
            const resolved = await Promise.all(invoices.map((invoice) => fetchInvoiceGroup(invoice, shownIds)));
            if (generationRef.current !== generation) return;
            setGroups(resolved.filter((group): group is InvoiceBillingEventGroup => group !== null));
        } catch (e) {
            if (generationRef.current !== generation) return;
            setError(failureMessage(e));
        } finally {
            if (generationRef.current === generation) setIsLoading(false);
        }
    }, [creditOperationId, chargeId]);

    useEffect(() => { load(); }, [load]);

    return { groups, isLoading, error, reload: load };
}
